import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Container, TextField, Table, TableBody, TableCell, Grid, TableContainer,
  Button, TableHead, TableRow, Typography, Paper, MenuItem, Checkbox, Autocomplete, IconButton, Box
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import ShareIcon from '@mui/icons-material/Share';
import LocalHospital from '@mui/icons-material/LocalHospital';
import { API_BASE_URL } from '../../lib/constants';
import theme from '../../theme/theme';
import { ThemeProvider } from '@mui/material/styles';
import DraftSaveButton from '../../components/DraftSaveButton';
import { useNotification } from '../../contexts/NotificationContext';
import { DRAFT_KEYS, hasDraft, loadDraft } from '../../utils/draftUtils';

// Define your API base URL here (adjust as needed)


const timeIntervals = [
  "Pre-Anesthesia Vital Signs", "Time", "Adm",
  "15 min", "30 min", "45 min", "1 hr", "2 hr", "3 hr", "4 hr", "Discharge"
];

const categories = [
  { name: "Circulation", options: ["Blood pressure >50mmHg of normal (0)", "Blood pressure ±20-50mmHg of normal (1)", "Blood pressure ±20mmHg of normal(2)"] },
  { name: "Oxygenation", options: ["SpO2 <90% on oxygen (0)", "SpO2 >90% on oxygen (1)", "SpO2 >92% on room air (2)"] },
  { name: "Respiration", options: ["Apnea (0)", "Dyspnea, shallow breathing (1)", "Breathes deeply & coughs freely (2)"] },
  { name: "Consciousness", options: ["Not responsive (0)", "Arousable on calling (1)", "Fully awake (2)"] },
  { name: "Activity", options: ["No movement (0)", "Moves two extremities (1)", "Moves all extremities (2)"] },
];

const timeValues = {
  "15 min": 15,
  "30 min": 30,
  "45 min": 45,
  "1 hr": 60,
  "2 hr": 120,
  "3 hr": 180,
  "4 hr": 240
};

export default function AldreteScoreForm() {
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const { uhid_no } = useParams(); // Extract uhid_no directly from params
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errors, setErrors] = useState({});

  const validateField = (name, value) => {
    let error = "";
    switch (name) {
      case 'surgery_procedure':
        if (!value || !value.trim()) error = "Surgery/Procedure is required";
        break;
      case 'anaesthesia':
        if (!value || !value.trim()) error = "Anesthesia is required";
        break;
      default:
        break;
    }
    setErrors((prev) => ({ ...prev, [name]: error }));
    return error === "";
  };

  const handleBlur = (event) => {
    const { name, value } = event.target;
    validateField(name, value);
  };

  const [formData, setFormData] = useState({
    patient_name: "",
    age: "",
    gender: "",
    uhid_no: uhid_no ? uhid_no : "",
    surgery_procedure: "",
    anaesthesia: "",
    totalScores: [],
    scores: categories.map(() => timeIntervals.map(() => 0)),
    selectedOptions: Array(categories.length * timeIntervals.length).fill(""),

    checkboxStates: categories.map(() => timeIntervals.map(() => false)),
    timeValues: categories.map(() => timeIntervals.map(() => "")),
    baseTime: Array(categories.length).fill(""),
    admTime: Array(categories.length).fill(""),
    dischargeTime: Array(categories.length).fill(""),
  });

  // Auto-load draft if present on mount
  useEffect(() => {
    if (uhid_no && hasDraft(DRAFT_KEYS.POST_OPERATIVE_ANALGESIA_ASSESSMENT, uhid_no)) {
      const saved = loadDraft(DRAFT_KEYS.POST_OPERATIVE_ANALGESIA_ASSESSMENT, uhid_no);
      if (saved && saved.formData) {
        setFormData(prev => ({ ...prev, ...saved.formData }));
      }
    }
  }, [uhid_no]);
  const handleNameChange = (e) => {
    const regex = /^[a-zA-Z\s.-]*$/; // allow letters, spaces, dot, hyphen
    const value = e.target.value;
    if (value === '' || regex.test(value)) {
      setFormData({ ...formData, [e.target.name]: value });
      if (errors[e.target.name]) {
        setErrors((prev) => ({ ...prev, [e.target.name]: '' }));
      }
    }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };
  const cleanedOptions = formData.selectedOptions.filter(
    option => typeof option === 'string' && option.trim() !== ""
  );
  console.log(cleanedOptions);

  const addMinutesToTime = (timeStr, minutesToAdd) => {
    if (typeof timeStr !== "string" || !timeStr.includes(":")) return "";
    const [hours, minutes] = timeStr.split(":").map(Number);
    const totalMinutes = hours * 60 + minutes + minutesToAdd;
    const newHours = Math.floor(totalMinutes / 60) % 24;
    const newMinutes = totalMinutes % 60;
    return `${newHours.toString().padStart(2, "0")}:${newMinutes.toString().padStart(2, "0")}`;
  };

  const handleCheckboxClick = (categoryIndex, timeIndex) => {
    const newCheckboxStates = [...formData.checkboxStates];
    const newTimeValues = [...formData.timeValues];

    newCheckboxStates[categoryIndex][timeIndex] = !newCheckboxStates[categoryIndex][timeIndex];

    if (newCheckboxStates[categoryIndex][timeIndex] && formData.baseTime[categoryIndex]) {
      const intervalStr = timeIntervals[timeIndex];
      const minutesToAdd = timeValues[intervalStr] || 0;

      if (minutesToAdd > 0) {
        newTimeValues[categoryIndex][timeIndex] = addMinutesToTime(formData.baseTime[categoryIndex], minutesToAdd);
      }
    } else {
      newTimeValues[categoryIndex][timeIndex] = "";
    }

    setFormData({
      ...formData,
      checkboxStates: newCheckboxStates,
      timeValues: newTimeValues
    });
  };

  const calculateTotalScores = () => {
    return timeIntervals.map((_, timeIndex) => {
      return categories.reduce((total, _, catIndex) => {
        if (formData.scores[catIndex] && formData.scores[catIndex][timeIndex] !== undefined) {
          return total + (formData.scores[catIndex][timeIndex] || 0);
        }
        return total;
      }, 0);
    });
  };


  const totalScores = formData.totalScores.length > 0
    ? formData.totalScores
    : calculateTotalScores();

  console.log("totalScores:", totalScores);


  const handleScoreChange = (catIndex, timeIndex, event) => {
    const newScore = parseInt(event.target.value, 10);
    const selectedOption = categories[catIndex].options[newScore];

    setFormData(prevFormData => {
      const newScores = [...prevFormData.scores];
      newScores[catIndex][timeIndex] = newScore;

      const newSelectedOptions = [...prevFormData.selectedOptions];
      newSelectedOptions[catIndex * timeIntervals.length + timeIndex] = selectedOption;

      return {
        ...prevFormData,
        scores: newScores,
        selectedOptions: newSelectedOptions
      };
    });
  };




  const validate = () => {
    const requiredFields = [
      { key: 'surgery_procedure', label: 'Surgery / Procedure' },
      { key: 'anaesthesia',       label: 'Anaesthesia Type'    },
    ];
    const newErrors = {};
    requiredFields.forEach(({ key, label }) => {
      if (!formData[key] || String(formData[key]).trim() === '')
        newErrors[key] = `${label} is required`;
    });
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      const firstKey = Object.keys(newErrors)[0];
      const el = document.querySelector(`[name="${firstKey}"]`);
      if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.focus(); }
      showNotification('error', requiredFields.filter(f => newErrors[f.key]).map(f => f.label));
    }
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validate()) return;

    const dataToSend = {
      patient_name: formData.patient_name,
      hospital_id: 1,
      age: formData.age,
      gender: formData.gender,
      uhid_no: formData.uhid_no,
      surgery_procedure: formData.surgery_procedure,
      anaesthesia: formData.anaesthesia,

      // Pre-Anesthesia Category Options
      circulation_option: formData.selectedOptions[0],
      oxygenation_option: formData.selectedOptions[timeIntervals.length * 1 + 0],
      respiration_option: formData.selectedOptions[timeIntervals.length * 2 + 0],
      consciousness_option: formData.selectedOptions[timeIntervals.length * 3 + 0],
      activity_option: formData.selectedOptions[timeIntervals.length * 4 + 0],

      // Discharge Times
      circulation_discharge_time: formData.dischargeTime[0],
      oxygenation_discharge_time: formData.dischargeTime[1],
      respiration_discharge_time: formData.dischargeTime[2],
      consciousness_discharge_time: formData.dischargeTime[3],
      activity_discharge_time: formData.dischargeTime[4],


      // Adm Scores
      circulation_adm: formData.admTime[0],
      oxygenation_adm: formData.admTime[1],
      respiration_adm: formData.admTime[2],
      consciousness_adm: formData.admTime[3],
      activity_adm: formData.admTime[4],

      // Adm Times
      circulation_adm_time: formData.baseTime[0],
      oxygenation_adm_time: formData.baseTime[1],
      respiration_adm_time: formData.baseTime[2],
      consciousness_adm_time: formData.baseTime[3],
      activity_adm_time: formData.baseTime[4],

      // Time-based Scores and Times
      // 15 min
      circulation_time_15: formData.checkboxStates[0][3],
      oxygenation_time_15: formData.checkboxStates[1][3],
      respiration_time_15: formData.checkboxStates[2][3],
      consciousness_time_15: formData.checkboxStates[3][3],
      activity_time_15: formData.checkboxStates[4][3],

      // 30 min
      circulation_time_30: formData.checkboxStates[0][4],
      oxygenation_time_30: formData.checkboxStates[1][4],
      respiration_time_30: formData.checkboxStates[2][4],
      consciousness_time_30: formData.checkboxStates[3][4],
      activity_time_30: formData.checkboxStates[4][4],

      // 45 min
      circulation_time_45: formData.checkboxStates[0][5],
      oxygenation_time_45: formData.checkboxStates[1][5],
      respiration_time_45: formData.checkboxStates[2][5],
      consciousness_time_45: formData.checkboxStates[3][5],
      activity_time_45: formData.checkboxStates[4][5],

      // 1 hr
      circulation_time_1h: formData.checkboxStates[0][6],
      oxygenation_time_1h: formData.checkboxStates[1][6],
      respiration_time_1h: formData.checkboxStates[2][6],
      consciousness_time_1h: formData.checkboxStates[3][6],
      activity_time_1h: formData.checkboxStates[4][6],

      // 2 hr
      circulation_time_2h: formData.checkboxStates[0][7],
      oxygenation_time_2h: formData.checkboxStates[1][7],
      respiration_time_2h: formData.checkboxStates[2][7],
      consciousness_time_2h: formData.checkboxStates[3][7],
      activity_time_2h: formData.checkboxStates[4][7],

      // 3 hr
      circulation_time_3h: formData.checkboxStates[0][8],
      oxygenation_time_3h: formData.checkboxStates[1][8],
      respiration_time_3h: formData.checkboxStates[2][8],
      consciousness_time_3h: formData.checkboxStates[3][8],
      activity_time_3h: formData.checkboxStates[4][8],

      // 4 hr
      circulation_time_4h: formData.checkboxStates[0][9],
      oxygenation_time_4h: formData.checkboxStates[1][9],
      respiration_time_4h: formData.checkboxStates[2][9],
      consciousness_time_4h: formData.checkboxStates[3][9],
      activity_time_4h: formData.checkboxStates[4][9],


      // Total Scores
      pre_anesthesia_vitalscore: totalScores[0],
      score_15: totalScores[3],
      score_30: totalScores[4],
      score_45: totalScores[5],
      score_1h: totalScores[6],
      score_2h: totalScores[7],
      score_3h: totalScores[8],
      score_4h: totalScores[9]
    };

    console.log("Final data to send to backend:", dataToSend);
    try {
      if (success) {
        await axios.put(`${API_BASE_URL}/update_alderete_score/${formData.uhid_no}`, dataToSend);
        showNotification('success', "Alderete Score Form updated successfully!");
      } else {
        await axios.post(`${API_BASE_URL}/insert_alderete_score`, dataToSend);
        showNotification('success', "Alderete Score Form submitted successfully!");
      }
      clearSavedData();
      navigate(`/mainPage/${formData.uhid_no}`, {
        state: { from: 'PostOperativePhase', status: 'success', step: 3 },
      });
    } catch (error) {
      console.error("There was an error submitting the form!", error);
      showNotification('error', "Error in submitting Alderete Score form!");
    }
  };
  const [savedData, setSavedData] = useState(null);
  const [showSaveNotification, setShowSaveNotification] = useState(false);
  const [currentPage, setCurrentPage] = useState('form');
  const [dataLoaded, setDataLoaded] = useState(false);
  // Auto-save functionality
  // Auto-save functionality - only after initial data is loaded
  useEffect(() => {
    if (!dataLoaded) return; // Don't auto-save until initial data is loaded

    const timeoutId = setTimeout(() => {
      if (hasFormData()) {
        saveFormData(formData);
        setShowSaveNotification(true);
        setTimeout(() => setShowSaveNotification(false), 2000);
      }
    }, 2000);

    return () => clearTimeout(timeoutId);
  }, [formData, dataLoaded]);

  // Save form data to sessionStorage
  const saveFormData = (data) => {
    sessionStorage.setItem(`alderte_scoreForm_${uhid_no}`, JSON.stringify(data));
    console.log('Form data saved:', data);
  };

  // Get saved form data
  const getSavedFormData = () => {
    const saved = sessionStorage.getItem(`alderte_scoreForm_${uhid_no}`);
    return saved ? JSON.parse(saved) : null;
  };
  useEffect(() => {
    const saved = getSavedFormData();
    if (saved && Object.keys(saved).length > 0) {
      setSavedData(saved);
      setFormData(saved);
    }
  }, []);
  // Check if form has any data
  const hasFormData = () => {
    return Object.values(formData).some(value => {
      if (typeof value === 'string') return value.trim() !== '';
      if (typeof value === 'object' && value !== null) {
        return Object.values(value).some(subValue =>
          typeof subValue === 'string' ? subValue.trim() !== '' : subValue
        );
      }
      return value === true;
    });
  };

  // Clear saved data - ONLY called on successful submit
  const clearSavedData = () => {
    setSavedData(null);
    sessionStorage.removeItem(`alderte_scoreForm_${uhid_no}`);
    sessionStorage.removeItem(`formStatus_alderte_score_${uhid_no}`);
  };

  // Home button handler
  const handleHomeNavigation = () => {
    if (hasFormData()) {
      saveFormData(formData);
      // Store form status for home page to show "Continue" button
      sessionStorage.setItem(`formStatus_alderte_score_${uhid_no}`, JSON.stringify({
        formName: 'ALDRETE SCORE',
        uhid_no: uhid_no,
        status: 'incomplete',
        lastSaved: new Date().toISOString(),
        continueUrl: `/AldreteScore/${uhid_no}`
      }));
    }
    navigate('/home');
  };
  const handleBack = () => {
    navigate(`/mainPage/${formData.uhid_no}`, {
      state: { from: 'PostOperativePhase', status: 'success', step: 3 },
    }); // Navigate to the previous page
  };
  const fetchPatientData = async (uhidNo) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/get_patient/${uhidNo}`);

      console.log(response.data);
      setFormData(prev => ({
        ...prev,

        patient_name: response.data.name,
        age: response.data.age || '',
        gender: response.data.sex || '',


      }));
      setLoading(false);
    } catch (error) {
      console.error('Error fetching patient data:', error);
      setError('Failed to fetch patient data. Please try again.');
      setLoading(false);
    }
  };
  useEffect(() => {
    if (!uhid_no) return;

    const fetchData = async () => {
      try {
        const { data } = await axios.get(`${API_BASE_URL}/get_alderete_score/${uhid_no}`);

        if (!data) return;


        // Initialize checkboxStates for times starting at index 3 ("15 min" and onwards)
        const timeKeyMap = {
          "15 min": "time_15",
          "30 min": "time_30",
          "45 min": "time_45",
          "1 hr": "time_1h",
          "2 hr": "time_2h",
          "3 hr": "time_3h",
          "4 hr": "time_4h",
        };

        const checkboxStates = categories.map((cat, catIndex) =>
          timeIntervals.map((time, timeIndex) => {
            if (timeIndex < 3) return false;
            const keySuffix = timeKeyMap[time];
            if (!keySuffix) return false;
            const catKey = cat.name.toLowerCase();
            return Boolean(data[`${catKey}_${keySuffix}`]);
          })
        );
        const optionsFromBackend = [
          data.circulation_option || "",
          data.oxygenation_option || "",
          data.respiration_option || "",
          data.consciousness_option || "",
          data.activity_option || ""
        ];

        // Now set selectedOptions for the "Pre-Anesthesia Vital Signs" column (timeIndex = 0) for each category
        // Other time intervals you can set empty string or from backend if available
        const selectedOptions = categories.flatMap((cat, catIndex) =>
          timeIntervals.map((time, timeIndex) => {
            if (time === "Pre-Anesthesia Vital Signs") {
              return optionsFromBackend[catIndex];
            }
            return ""; // or fill as needed for other timeIntervals
          })
        );

        // Map scores from selectedOptions by finding option index per category
        const scores = categories.map((cat, catIndex) =>
          timeIntervals.map((time, timeIndex) => {
            if (time === "Pre-Anesthesia Vital Signs") {
              const opt = optionsFromBackend[catIndex];
              const idx = cat.options.findIndex(o => o === opt);
              return idx >= 0 ? idx : 0;
            }
            return 0; // or map other times if needed
          })
        );
        // Base, adm and discharge times
        const baseTime = categories.map((cat, i) => data[`${cat.name.toLowerCase()}_adm_time`] || "");
        const admTime = categories.map((cat, i) => data[`${cat.name.toLowerCase()}_adm`] || "");
        const dischargeTime = categories.map((cat, i) => data[`${cat.name.toLowerCase()}_discharge_time`] || "");
        const totalScoresFromBackend = [
          data.pre_anesthesia_vitalscore || 0,   // index 0 might be Pre-Anesthesia or placeholder if needed
          0,  // index 1 (Time)
          0,  // index 2 (Adm)
          data.score_15 || 0,
          data.score_30 || 0,
          data.score_45 || 0,
          data.score_1h || 0,
          data.score_2h || 0,
          data.score_3h || 0,
          data.score_4h || 0,
          0,  // Discharge if any
        ];

        console.log("totalScoresFromBackend", totalScoresFromBackend);
        setFormData(prev => ({
          ...prev,
          patient_name: data.patient_name || "",
          age: data.age || "",
          gender: data.gender || "",
          uhid_no: data.uhid || uhid_no,
          surgery_procedure: data.surgery_procedure || "",
          anaesthesia: data.anaesthesia || "",
          scores,
          selectedOptions,
          checkboxStates,
          baseTime,
          admTime,
          dischargeTime,
          totalScores: totalScoresFromBackend,
        }));

        setSuccess(true);
      } catch (error) {
        console.error("Failed to fetch alderete score data", error);
      }
    };

    fetchData();
    fetchPatientData(uhid_no);
  }, [uhid_no]);

  // Fetch next UHID & IP_No on component mount
  // useEffect(() => {
  //   const fetchInitialData = async () => {
  //     try {
  //       const [uhidRes] = await Promise.all([
  //         axios.get('${API_BASE_URL}/postop_handover_next_uhid'),
  //        ]);
  //       setFormData(prev => ({
  //   ...prev,
  //   uhid: uhidRes.data.next_uhid // match the field in your formData
  // }));
  //     } catch (error) {
  //       console.error('Failed to fetch initial data:', error);
  //     }
  //   };

  //   fetchInitialData();
  // }, []);

  const surgeryProcedureOptions = [
    "Appendectomy",
    "Cholecystectomy",
    "Hernia Repair",
    "Knee Replacement"
  ];

  const anesthesiaOptions = [
    "General Anesthesia",
    "Spinal Anesthesia",
    "Epidural",
    "Local Anesthesia"
  ];
  const goToVideoRecorder = (formName, uhid_no) => {
    navigate(`/video?form=${encodeURIComponent(formName)}&uhid_no=${encodeURIComponent(uhid_no)}`);
  };
  const [submitStatus, setSubmitStatus] = useState({
    open: false,
    message: "",
    severity: "success"
  });
  const handleShareClick = async () => {
    try {
      // Call API to generate a share token for the given UHID
      const res = await axios.post(`${API_BASE_URL}/generate_for_alderete_api_share_token/${uhid_no}`);

      const token = res.data.token;
      const link = `${API_BASE_URL}/intraOperativeNursingRecord/${uhid_no}?token=${token}`;
      const message = `Dear Doctor,\n\nPlease fill the post-operative analgesia chart:\n\n${link}`;

      // Copy the shareable link to clipboard
      await navigator.clipboard.writeText(link);

      // Show success notification to user
      setSubmitStatus({
        open: true,
        message: "Secure link copied to clipboard!",
        severity: "info",
      });

      // Open WhatsApp chat with pre-filled message (replace phone number if needed)
      const phoneNumber = '+916379605575'; // no spaces or special chars for wa.me
      window.open(`http://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`, '_blank');
    } catch (error) {
      console.error("Error generating share token:", error);

      // Show error notification to user
      setSubmitStatus({
        open: true,
        message: "Failed to generate shareable link",
        severity: "error",
      });
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="md" sx={{ marginTop: 4, marginBottom: 4 }}>
        <Paper elevation={3} sx={{ padding: 3 }}>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 2 }}>
            {/* Heading with Icon */}
            <Typography
              variant="h4"
              sx={{
                color: '#008080',
                display: 'flex',
                alignItems: 'center',
                fontWeight: 600,
                letterSpacing: 0.5,
              }}
            >

              ALDRETE SCORE
            </Typography>

            {/* Right-aligned Logo and Text */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <img src="/logo.png" alt="Logo" className="w-8 h-8" />
              <Typography
                variant="h6"
                sx={{
                  color: '#008080',
                  marginLeft: 1,
                  fontWeight: 500,
                  letterSpacing: 0.3,
                }}
              >
                OTMS
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
            {/* Left-aligned Back Button */}
            <Button
              variant="outlined"
              onClick={handleBack}
              sx={{ marginLeft: 2 }}
            >
              Back
            </Button>

            {/* Right-aligned Buttons */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Button
                variant="contained"
                onClick={handleHomeNavigation}
                sx={{ marginRight: 1 }}
              >
                Home
              </Button>
              <Button
                variant="contained"
                onClick={() => goToVideoRecorder("ALDRETE SCORE", uhid_no)}
                sx={{ marginRight: 1 }}
              >
                Add Iterative Video
              </Button>
              <IconButton color="primary" aria-label="share" onClick={handleShareClick}>
                <ShareIcon />
              </IconButton>
            </Box>
          </Box>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Patient Name"
                  name="patient_name"
                  InputProps={{ readOnly: true, }}
                  value={formData.patient_name}
                  onChange={handleNameChange}
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  label="Age"
                  name="age"
                  type="number"
                  InputProps={{ readOnly: true, }}
                  value={formData.age}
                  onChange={handleInputChange}
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  select
                  label="Gender"
                  name="gender"
                  value={formData.gender}
                  onChange={handleInputChange}
                  required
                  InputProps={{ readOnly: true, }}
                  fullWidth
                >
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="UHID "
                  name="uhid_no"
                  value={formData.uhid_no}
                  onChange={handleInputChange}
                  required
                  fullWidth
                  InputProps={{ readOnly: true, }}

                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  freeSolo
                  options={surgeryProcedureOptions}
                  value={formData.surgery_procedure || ""}
                  onInputChange={(event, newValue) => {
                    setFormData({ ...formData, surgery_procedure: newValue });
                    if (errors.surgery_procedure) setErrors(prev => ({ ...prev, surgery_procedure: "" }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Surgery/Procedure Done"
                      name="surgery_procedure"
                      required
                      fullWidth
                      error={!!errors.surgery_procedure}
                      helperText={errors.surgery_procedure}
                      onBlur={handleBlur}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Autocomplete
                  freeSolo
                  options={anesthesiaOptions}
                  value={formData.anaesthesia || ""}
                  onInputChange={(event, newValue) => {
                    setFormData({ ...formData, anaesthesia: newValue });
                    if (errors.anaesthesia) setErrors(prev => ({ ...prev, anaesthesia: "" }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Anesthesia"
                      name="anaesthesia"
                      required
                      fullWidth
                      error={!!errors.anaesthesia}
                      helperText={errors.anaesthesia}
                      onBlur={handleBlur}
                    />
                  )}
                />
              </Grid>

            </Grid>

            <TableContainer component={Paper} sx={{ marginTop: 3 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><b>Category</b></TableCell>
                    {timeIntervals.map((time) => (
                      <TableCell key={time} align="center"><b>{time}</b></TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {categories.map((cat, catIndex) => (
                    <TableRow key={cat.name}>
                      <TableCell><b>{cat.name}</b></TableCell>
                      {timeIntervals.map((time, timeIndex) => (
                        <TableCell key={timeIndex} align="center">
                          {time === "Pre-Anesthesia Vital Signs" ? (
                            <TextField
                              select
                              size="small"
                              fullWidth
                              value={formData.scores[catIndex][timeIndex]}   // This will be 0, 1, or 2 (for example)
                              onChange={(e) => handleScoreChange(catIndex, timeIndex, e)}
                            >
                              {cat.options.map((option, optionIndex) => (
                                <MenuItem key={optionIndex} value={optionIndex}>
                                  {option}
                                </MenuItem>
                              ))}
                            </TextField>



                          ) : time === "Time" ? (
                            <TextField
                              type="time"
                              size="small"
                              value={formData.baseTime[catIndex] || ""}
                              onChange={(e) => {
                                const newBaseTimes = [...formData.baseTime];
                                newBaseTimes[catIndex] = e.target.value;
                                setFormData({ ...formData, baseTime: newBaseTimes });
                              }}
                              fullWidth
                            />
                          ) : time === "Adm" ? (
                            <TextField
                              type="text"
                              size="medium"
                              variant="standard"
                              value={formData.admTime[catIndex] || ""}
                              onChange={(e) => {
                                const newAdmTimes = [...formData.admTime];
                                newAdmTimes[catIndex] = e.target.value;
                                setFormData({ ...formData, admTime: newAdmTimes });
                              }}
                              fullWidth
                            />
                          ) : time === "Discharge" ? (
                            <TextField
                              type="time"
                              size="small"
                              value={formData.dischargeTime[catIndex] || ""}
                              onChange={(e) => {
                                const newDischargeTimes = [...formData.dischargeTime];
                                newDischargeTimes[catIndex] = e.target.value;
                                setFormData({ ...formData, dischargeTime: newDischargeTimes });
                              }}
                              fullWidth
                            />
                          ) : (
                            <div>
                              <Checkbox
                                checked={formData.checkboxStates[catIndex][timeIndex]}
                                onChange={() => handleCheckboxClick(catIndex, timeIndex)}
                              />
                              {formData.timeValues[catIndex][timeIndex] && (
                                <Typography variant="caption" display="block">
                                  {formData.timeValues[catIndex][timeIndex]}
                                </Typography>
                              )}
                            </div>
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell><b>Total Score</b></TableCell>
                    {totalScores.map((total, index) => (
                      <TableCell key={index} align="center">
                        {index === 1 ? '' : index === 2 ? '' : index === 10 ? '' : total}
                      </TableCell>
                    ))}
                  </TableRow>

                </TableBody>
              </Table>
            </TableContainer>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <DraftSaveButton
                  formKey={DRAFT_KEYS.POST_OPERATIVE_ANALGESIA_ASSESSMENT}
                  uhidNo={uhid_no}
                  formData={formData}
                  onDraftLoaded={(draftData) => setFormData(prev => ({ ...prev, ...draftData }))}
                  variant="outlined"
                  size="large"
                  isUpdate={success}
                />
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  sx={{ marginTop: 3 }}
                  fullWidth
                >
                  {success ? 'Update' : 'Submit'}
                </Button>
              </Box>
            </Grid>
          </form>
        </Paper>
      </Container>
    </ThemeProvider>
  );
}
